import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTitle } from '../../redux/title/action';
import { getItemsQuery, useFormState } from './hooks';
import { HomeNavbarView } from './views/navbar/HomeNavbarView';
import { LoadingPage } from '../../components/loading';
import ErrorScene from '../error';
import { useQueryClient } from '@tanstack/react-query';
import { WaitConfirmView } from './views/contents/warranties/WaitConfirmView';
import { WaitClaimView } from './views/contents/claims/WaitClaimView';
import { HomeTabContentView } from './views/tab/HomeTabContentView';
import { TransportedView } from './views/contents/transported/TransportedView';

export const HomeScene = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams(window.location.search);
  const tab = useMemo(() => queryParams.get('tab'), [queryParams]);
  const userProfile = useSelector((state) => state.info);

  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    pageSize: 10,
    total: 0,
  });

  const { data, isLoading, error, refetch } = getItemsQuery(userProfile?.lineUid);
  const storeName = data ? data?.dealer?.storeName : '-';
  const { tabContents, tabActive } = useFormState({
    tab,
    waitConfirmScene: null,
    waitClaimScene: null,
    claimedScene: null,
  });

  const [activeKey, setActiveKey] = useState(tabActive?.key);
  const [isRefetching, setIsRefetching] = React.useState(true);

  const handlePageChange = useCallback((page, pageSize) => {
    setPaginationState((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: pageSize || prev.pageSize,
    }));
  }, []);

  const handleRefetch = useCallback(async () => {
    setIsRefetching(true);
    await queryClient.removeQueries(['getItemsQuery']);
    await refetch();
    setIsRefetching(false);
  }, [queryClient, refetch]);

  useEffect(() => {
    dispatch(setTitle(`สาขา ${storeName}`));
  }, [dispatch, storeName]);

  useEffect(() => {
    if (userProfile && userProfile.lineUid) handleRefetch();
  }, [window.location.search, handleRefetch, userProfile]);

  const contents = useMemo(() => {
    if (!data) return tabContents;

    const updatePaginationTotal = (items) => {
      if (items && Array.isArray(items)) {
        setPaginationState((prev) => ({ ...prev, total: items.length }));
      }
      return items;
    };

    useEffect(() => {
      if (data) {
        if (activeKey === '1' && data?.claimsWaiting) {
          updatePaginationTotal(data?.claimsWaiting);
        } else if (activeKey === '2' && data?.claimsProcessing) {
          updatePaginationTotal(data?.claimsProcessing);
        } else if (activeKey === '3' && data?.claimsTransported) {
          updatePaginationTotal(data?.claimsTransported);
        }
      }
    }, [data, activeKey]);

    let contentList = [
      {
        ...tabContents[0],
        count: data?.summary.totalClaimWaiting ?? 0,
        children: (
          <WaitConfirmView
            items={data?.claimsWaiting}
            pagination={paginationState}
            onPageChange={handlePageChange}
          />
        ),
      },
      {
        ...tabContents[1],
        count: data?.summary.totalClaimProcesseing ?? 0,
        children: (
          <WaitClaimView
            items={data?.claimsProcessing}
            pagination={paginationState}
            onPageChange={handlePageChange}
          />
        ),
      },
      {
        ...tabContents[2],
        count: data?.summary.totalClaimTranported ?? 0,
        children: (
          <TransportedView
            items={data?.claimsTransported}
            pagination={paginationState}
            onPageChange={handlePageChange}
          />
        ),
      },
    ];
    return contentList;
  }, [data, tabContents, paginationState, handlePageChange, activeKey]);

  const onChange = useCallback((key) => {
    setActiveKey(key);
    setPaginationState((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  if (isLoading || isRefetching) {
    return <LoadingPage loading={true} />;
  }

  if (error) {
    return <ErrorScene />;
  }

  return (
    <div>
      <HomeNavbarView activeKey={activeKey} onChange={onChange} tabContents={contents} />
      <HomeTabContentView activeKey={activeKey} onChange={onChange} tabContents={contents} />
    </div>
  );
};
